import cx from 'classnames';
import { t } from 'i18next';
import _ from 'lodash';
import { Divider, MenuItem, SubMenu } from 'rc-menu';
import { FC, ReactElement } from 'react';
import { SpecialTags } from '../../../../shared/enums';
import { TArticleContentSummaryInfo } from '../../../../shared/newsArticleShared';
import { namedRoutes, resolveRoutePath } from '../../bundles/routes';
import frontendStore from '../../../../../../shared/frontend/libs/frontendStore';
import { handleRCTitleMenuOnClick } from '../../../../../../shared/frontend/libs/history';
import { findBySlug, sortByName } from '../../../../../../shared/frontend/libs/utils';
import { getRegion } from '../container/raidFloatingMenu';
import { ClassIcon } from '../../../../../../shared/frontend/scripts/wow/components/ClassIcon';
import { DropdownTitle } from './dropdownTitle';
import WowIcon from '../../../../../../shared/frontend/scripts/wow/components/WowIcon';

export enum EMenuEntry {
  ITEM = 'item',
  DIVIDER = 'divider'
}

type TMenuEntryDivider = {
  type: EMenuEntry.DIVIDER;
};

type TChildrenGenerator = Function; // (entry: TMenuEntry) => TMenuEntry[];

type TMenuEntryItem = {
  type: EMenuEntry.ITEM;
  title: any;
  titleClass?: string;
  link?: string;
  className?: string;
  children?: TMenuEntry[] | TChildrenGenerator;
  customIcon?: string;
  customIconComponent?: ReactElement;
  iconClassName?: string; // TODO: remove and only use customIcon?
  wowIcon?: string | string[]; // TODO: remove and only use custom Icon?
  isNew?: boolean;
  isBeta?: boolean;
  isLive?: boolean;
  wowIcons?: string[];
  postIconClass?: string; // class to put on a suffix after title
  onClick?: () => any;

  // these can bse used if you need more flexibility than the provided icons.
  PreIcon?: FC;
  PostIcon?: FC;
};

export type TMenuEntry = TMenuEntryDivider | TMenuEntryItem;

export function buildMenus(prefix: string, menus: TMenuEntry[], params?: any) {
  const { handleSubMenuClick = handleRCTitleMenuOnClick } = params || {};
  const items: any[] = [];
  menus.forEach((menu: TMenuEntry, index: number) => {
    if (menu.type === EMenuEntry.DIVIDER) {
      items.push(<Divider key={index} />);
    } else if (menu.type === EMenuEntry.ITEM) {
      let titleData: any;

      if (typeof menu.title === 'string') {
        titleData = t(menu.title);
      } else if (typeof menu.title === 'function') {
        titleData = menu.title(menu);
      } else {
        titleData = menu.title;
      }

      const titleLine: any = (
        <>
          {menu.iconClassName && <div className={cx(menu.iconClassName, 'slds-m-right--x-small')} />}
          {menu.wowIcon && (
            <>
              {([] as string[]).concat(menu.wowIcon).map((icon, iconIndex) => {
                return <WowIcon lazy icon={icon} size="xx-small" key={iconIndex} className="slds-m-right--x-small" />;
              })}
            </>
          )}
          {menu.customIcon && (
            <span className="slds-avatar slds-avatar--xx-small rio-border--light rio-shadow--small slds-m-right--x-small">
              <img loading="lazy" src={menu.customIcon} />
            </span>
          )}
          {menu.customIconComponent && (
            <span className="slds-avatar slds-avatar--xx-small rio-border--light rio-shadow--small slds-m-right--x-small">
              {menu.customIconComponent}
            </span>
          )}

          {menu.PreIcon && <menu.PreIcon />}
          <span className={menu.titleClass}>{titleData}</span>
          {menu.PostIcon && <menu.PostIcon />}

          {menu.postIconClass && <i className={menu.postIconClass} />}
          {menu.isNew && <span className="slds-m-left--x-small new-feature-indicator">{t('nav:suffixes.new')}</span>}
          {menu.isBeta && <span className="slds-m-left--x-small new-feature-indicator">{t('nav:suffixes.beta')}</span>}
          {menu.isLive && <span className="slds-m-left--x-small rwf-live-indicator">{t('nav:suffixes.live')}</span>}
        </>
      );

      if (menu.children) {
        let childMenus = null;
        if (_.isArray(menu.children) && menu.children.length > 0) {
          childMenus = buildMenus(`${prefix}:child${index}`, menu.children, params);
        } else if (typeof menu.children === 'function') {
          childMenus = buildMenus(`${prefix}:child${index}`, menu.children(menu), params);
        }

        items.push(
          <SubMenu
            key={`${prefix}:${index}`}
            onTitleClick={(domEvent: any) => {
              handleSubMenuClick(menu.link, domEvent);
              if (menu.onClick) {
                menu.onClick();
              }
            }}
            title={<span className={menu.className}>{titleLine}</span>}
          >
            {childMenus}
          </SubMenu>
        );
      } else {
        items.push(
          <MenuItem key={menu.link} onClick={menu.onClick}>
            <span className={menu.className}>{titleLine}</span>
          </MenuItem>
        );
      }
    }
  });
  return items;
}

export function getRaidRegionSubMenuItemsMenuEntry(props: any): TMenuEntry {
  let regionsChildren: TMenuEntry[] = [
    {
      type: EMenuEntry.ITEM,
      title: t('regions:world.name'),
      link: resolveRoutePath(props.path, buildRouteParams(props.match.params, { noRealm: true, region: 'world' }))
    },
    { type: EMenuEntry.DIVIDER },
    {
      type: EMenuEntry.ITEM,
      title: t('regions:americas.name'),
      link: resolveRoutePath(props.path, buildRouteParams(props.match.params, { noRealm: true, region: 'americas' })),
      children: buildSubMenu(
        [
          ['americas', t('nav:regions.all-americas')],
          ['north-america', t('regions:north-america.name')],
          ['south-america', t('regions:south-america.name')],
          ['brazil', t('regions:portuguese.name')],
          ['us-spanish', t('regions:spanish.name')],
          ['us-central', t('regions:us-central.name')],
          ['us-eastern', t('regions:us-eastern.name')],
          ['us-mountain', t('regions:us-mountain.name')],
          ['us-pacific', t('regions:us-pacific.name')]
        ],
        props.path,
        props.match.params
      )
    },
    { type: EMenuEntry.DIVIDER },
    {
      type: EMenuEntry.ITEM,
      title: t('regions:eu.name'),
      link: resolveRoutePath(props.path, buildRouteParams(props.match.params, { noRealm: true, region: 'eu' })),
      children: buildSubMenu(
        [
          ['eu', t('nav:regions.all-europe')],
          ['eu-english', t('regions:english.name')],
          ['french', t('regions:french.name')],
          ['german', t('regions:german.name')],
          ['italian', t('regions:italian.name')],
          ['eu-portuguese', t('regions:portuguese.name')],
          ['russian', t('regions:russian.name')],
          ['eu-spanish', t('regions:spanish.name')]
        ],
        props.path,
        props.match.params
      )
    }
  ];

  const miscRegions: TMenuEntry[] = [
    ['brazil', t('regions:brazil.name')],
    ['oceanic', t('regions:oceanic.name')],
    ['kr', t('regions:kr.name')],
    ['tw', t('regions:tw.name')]
  ].map(([slug, name]) => ({
    type: EMenuEntry.ITEM,
    title: name,
    link: resolveRoutePath(props.path, buildRouteParams(props.match.params, { noRealm: true, region: slug }))
  }));

  regionsChildren = regionsChildren.concat(miscRegions);

  regionsChildren = [
    ...regionsChildren,
    { type: EMenuEntry.DIVIDER },
    {
      type: EMenuEntry.ITEM,
      title: t('nav:regions.global-languages'),
      children: buildSubMenu(
        [
          ['tw', t('nav:regions:chinese')],
          ['english', t('regions:english.name')],
          ['french', t('regions:french.name')],
          ['german', t('regions:german.name')],
          ['italian', t('regions:italian.name')],
          ['kr', t('nav:regions.korean')],
          ['portuguese', t('regions:portuguese.name')],
          ['russian', t('regions:russian.name')],
          ['spanish', t('regions:spanish.name')]
        ],
        props.path,
        props.match.params
      )
    },
    {
      type: EMenuEntry.ITEM,
      title: t('nav:regions.wow-regions'),
      children: buildSubMenu(
        [
          ['us', t('regions:us.name')],
          ['eu', t('regions:eu.name')],
          ['kr', t('regions:kr.name')],
          ['tw', t('regions:tw.name')],
          ['cn', t('nav:regions.china-limited')]
        ],
        props.path,
        props.match.params
      )
    }
  ];

  const baseMenu = {
    type: EMenuEntry.ITEM,
    title: (
      <DropdownTitle
        title={
          props.match.params.region
            ? t(`regions:${getRegion(props.combinedRegions, props.match.params.region).slug}.name`)
            : 'Region'
        }
      />
    ),
    children: regionsChildren
  };

  return baseMenu;
}

export function getClass(classes: any, cls: any) {
  return findBySlug(classes, cls.toLowerCase());
}

export function formatAffixesRoute(affixes: string) {
  return affixes
    .split('-')
    .map((afx: any) => _.capitalize(afx))
    .join(', ');
}

export function sortAffixGroupAlpha(a: any, b: any) {
  if (a.name < b.name) {
    return -1;
  } else if (a.name > b.name) {
    return 1;
  }
  return 0;
}

export function buildRouteParams(
  current: Record<any, any>,
  overrides?: Record<any, any>,
  resetPagination: boolean = true
) {
  if (resetPagination && overrides && !overrides.page) {
    current = _.omit(current, ['page']);
  }
  const state = frontendStore.getState<any, any>();
  return _.extend(
    {
      // for raids
      raid: state.siteConfig.currentRaidSlug,
      difficulty: state.siteConfig.currentRaidDifficulty,
      region: 'world',

      // for M+
      dungeon: 'all',
      class: 'all',
      role: 'all'
    },
    _.omitBy(current, _.isNil),
    overrides
  );
}

interface IGetArticleSubMenuItemProps {
  type: SpecialTags;
  isSelected: boolean;
  isLatestArticle: boolean;
  article: TArticleContentSummaryInfo;
}

export function getArticleSubMenuItem(props: IGetArticleSubMenuItemProps) {
  switch (props.type) {
    default: {
      return null;
    }
  }
}

type TSlugWithTitle = [string, string][];

function buildSubMenu(slugWithTitle: TSlugWithTitle, path: string, params: any): TMenuEntry[] {
  return slugWithTitle.map(([slug, name]) => ({
    type: EMenuEntry.ITEM,
    link: resolveRoutePath(path, buildRouteParams(params, { noRealm: true, region: slug })),
    title: name
  }));
}

export function getCharacterSubMenuItems(props: any, opt?: any): TMenuEntry[] {
  const path = opt && opt.path ? opt.path : namedRoutes.CharacterRankings;
  const sortedClasses = [...props.classes].sort(sortByName);
  return [
    {
      type: EMenuEntry.ITEM,
      title: t('nav:all-classes'),
      link: resolveRoutePath(path, buildRouteParams({ ...props.match.params, class: 'all' }))
      // children: [
      //   {
      //     type: EMenuEntry.ITEM,
      //     title: t('nav:all-roles'),
      //     link: resolveRoutePath(path, buildRouteParams({ ...props.match.params, class: 'all' })),
      //     iconClassName: 'icon-role icon-x-small any-lfg'
      //   },
      //   {
      //     type: EMenuEntry.DIVIDER
      //   },
      //   // {
      //   //   type: EMenuEntry.ITEM,
      //   //   title: t('nav:all-role-plural', {
      //   //     role: t('common:roles.tank.name', { count: 100 })
      //   //   }),
      //   //   link: resolveRoutePath(path, buildRouteParams({ ...props.match.params, class: 'all', role: 'tank' })),
      //   //   iconClassName: 'icon-role icon-x-small tank-lfg'
      //   // },
      //   // {
      //   //   type: EMenuEntry.ITEM,
      //   //   title: t('nav:all-role-plural', {
      //   //     role: t('common:roles.healer.name', { count: 100 })
      //   //   }),
      //   //   link: resolveRoutePath(path, buildRouteParams({ ...props.match.params, class: 'all', role: 'healer' })),
      //   //   iconClassName: 'icon-role icon-x-small healer-lfg'
      //   // },
      //   // {
      //   //   type: EMenuEntry.ITEM,
      //   //   title: t('nav:all-role-plural', {
      //   //     role: t('common:roles.dps.name', { count: 100 })
      //   //   }),
      //   //   link: resolveRoutePath(path, buildRouteParams({ ...props.match.params, class: 'all', role: 'dps' })),
      //   //   iconClassName: 'icon-role icon-x-small dps-lfg'
      //   // },
      //   // {
      //   //   type: EMenuEntry.DIVIDER
      //   // }
      // ]
    },
    {
      type: EMenuEntry.DIVIDER
    },
    ...sortedClasses.map<TMenuEntry>((cls: any) => {
      // const canHeal = canPerformRole(cls.id, 'healer');
      // const canTank = canPerformRole(cls.id, 'tank');

      const item: TMenuEntry = {
        type: EMenuEntry.ITEM,
        link: resolveRoutePath(path, buildRouteParams({ ...props.match.params, class: cls.slug, page: 0 })),
        title: t(`game:classes.${cls.slug}.name`),
        customIconComponent: <ClassIcon class={cls.slug} />,
        titleClass: `class-color--${cls.id}`
      };

      // if (canHeal || canTank) {
      //   item.children = [];

      //   item.children.push({
      //     type: EMenuEntry.ITEM,
      //     link: resolveRoutePath(path, buildRouteParams({ ...props.match.params, class: cls.slug })),
      //     title: t('nav:all-roles'),
      //     iconClassName: 'icon-role icon-x-small any-lfg'
      //   });

      //   item.children.push({
      //     type: EMenuEntry.DIVIDER
      //   });

      //   if (canTank) {
      //     item.children.push({
      //       type: EMenuEntry.ITEM,
      //       link: resolveRoutePath(path, buildRouteParams({ ...props.match.params, class: cls.slug, role: 'tank' })),
      //       title: t('common:roles.tank.name', { count: 100 }),
      //       iconClassName: 'icon-role icon-x-small tank-lfg'
      //     });
      //   }

      //   if (canHeal) {
      //     item.children.push({
      //       type: EMenuEntry.ITEM,
      //       link: resolveRoutePath(path, buildRouteParams({ ...props.match.params, class: cls.slug, role: 'healer' })),
      //       title: t('common:roles.healer.name', { count: 100 }),
      //       iconClassName: 'icon-role icon-x-small healer-lfg'
      //     });
      //   }

      //   if (canHeal || canTank) {
      //     item.children.push({
      //       type: EMenuEntry.ITEM,
      //       link: resolveRoutePath(path, buildRouteParams({ ...props.match.params, class: cls.slug, role: 'dps' })),
      //       title: t('common:roles.dps.name', { count: 100 }),
      //       iconClassName: 'icon-role icon-x-small dps-lfg'
      //     });
      //   }
      // }

      return item;
    })
  ];
}
